import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 2 }

import { ref, computed } from 'vue';
import { formatDate } from '@/utils/date-utils';
import { useMutation } from '@vue/apollo-composable';
import { ApiKeyUpdateDocument, ApiKeyType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BBadge,
  BCard,
  BFormCheckbox,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';

// --- Props for user details ---

export default /*@__PURE__*/_defineComponent({
  __name: 'UserListOfApiKeys',
  props: {
    apiKeys: {},
    loading: { type: Boolean }
  },
  emits: ['apiKeyRevoked'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// --- Emit when access token revoked ---
const emits = __emit;

// --- Local state for API token update ---
const updatingToken = ref(false);
const updateTokenError = ref<string | null>(null);
const updateTokenSuccess = ref(false);
const updateTokenShowSuccessIndicator = ref(false);

// --- Mutation to update API token ---
const {
  mutate: updateApiKey,
  onDone: onUpdateTokenDone,
  onError: onUpdateTokenError,
} = useMutation(ApiKeyUpdateDocument);

// When the create mutation returns, show a success message and refetch tokens.
onUpdateTokenDone((res) => {
  console.debug('Token update done, response:', res);
  if (res.data && res.data.apiKeyUpdate) {
    updateTokenError.value = null;
    updateTokenShowSuccessIndicator.value = true;
    updateTokenSuccess.value = true;

    setTimeout(() => {
      updateTokenShowSuccessIndicator.value = false;
      showApiKeyRevokeModal.value = false;
    }, 2000);

    emits('apiKeyRevoked'); // emit to refresh active access tokens upstream
  } else {
    console.error('Token update failed, missing token data:', res.data);
    updateTokenSuccess.value = false;
    updateTokenShowSuccessIndicator.value = false;
    updateTokenError.value =
      'Token update failed, response is missing the token data: ' + res.data;
  }
  updatingToken.value = false;
  apiKeyIdToRemove.value = null;
});

// Handle any errors during token creation.
onUpdateTokenError((err) => {
  console.error('Token update error:', err);
  updateTokenError.value = err.message;
  updateTokenSuccess.value = false;
  updateTokenShowSuccessIndicator.value = false;
  updatingToken.value = false;
});

// Function to handle token creation form submission.
function handleRevokeToken(id: number) {
  // Clear previous messages
  updateTokenError.value = null;
  updateTokenSuccess.value = false;
  updateTokenShowSuccessIndicator.value = false;
  updatingToken.value = true;

  updateApiKey({
    id: id,
    revokedAt: new Date(Date.now()),
  });
}

// --- List of keys ---
const isKeyInactive = (key: ApiKeyType | null): boolean => {
  if (!key) return true;
  return !!key.revokedAt || (!!key.expiresAt && key.expiresAt < new Date());
};

const showInactiveApiKeys = ref(false);

const filteredApiKeys = computed(() => {
  if (!props.apiKeys) return [];

  if (showInactiveApiKeys.value) {
    // Show all keys, including revoked ones
    return props.apiKeys.map((key) => ({
      ...key, // Spread original key properties
      _rowVariant: getRowVariant(key), // Assign row variant dynamically
    }));
  } else {
    // Hide revoked keys
    return props.apiKeys.filter((key) => !isKeyInactive(key));
  }
});

const getRowVariant = (item: ApiKeyType | null): string => {
  if (!item) return '';
  if (isKeyInactive(item)) {
    return 'danger';
  }
  return 'success';
};

// --- API Key Table fields (hide revokedAt if showInactiveApiKeys is false) ---
const apiKyesTablefields = computed(() => {
  const fields = [
    {
      key: 'keyName',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'permissions',
      label: 'Permissions',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'expiresAt',
      label: 'Expiration',
      sortable: true,
      formatter: (v: Date | string | undefined | null) => formatDate(v, '-'),
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (showInactiveApiKeys.value) {
    fields.splice(5, 0, {
      key: 'revokedAt',
      label: 'Revoked',
      sortable: true,
      formatter: (v) => formatDate(v, '-'),
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Refs to handle the API key revoke modal
const showApiKeyRevokeModal = ref(false);
const apiKeyIdToRemove = ref<number | null>(null);

function revokeApiKeyButtonClicked(id: number) {
  console.debug('Revoke button clicked for key:', id);
  apiKeyIdToRemove.value = id;
  showApiKeyRevokeModal.value = true;
}

function revokeApiKeyConfirmed(event: Event) {
  // Prevent the modal from closing
  event.preventDefault();

  // This happens when clicked modal button multiple times while update is in progress
  if (apiKeyIdToRemove.value == null) {
    console.error('No API key ID to revoke');
    return;
  }

  // Call the mutation to revoke the token
  handleRevokeToken(apiKeyIdToRemove.value);
}

function apiKeyVariant(permissions: string): string {
  if (permissions.includes('admin')) {
    return 'primary';
  } else if (permissions.includes('write')) {
    return 'success';
  } else if (permissions.includes('read')) {
    return 'success';
  } else if (permissions.includes('agent')) {
    return 'warning';
  }
  return 'secondary';
}

function apiKeyIcon(permissions: string): string {
  if (permissions.includes('admin')) {
    return 'fas fa-shield-halved';
  } else if (permissions.includes('write')) {
    return 'fas fa-pencil-alt';
  } else if (permissions.includes('read')) {
    return 'fas fa-eye';
  } else if (permissions.includes('agent')) {
    return 'fas fa-user-secret';
  }
  return 'fas fa-question';
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (!filteredApiKeys.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("h5", { class: "my-3 py-3 text-center" }, " You have no personal access tokens to show. ", -1)
        ]))]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(BCard), null, {
            default: _withCtx(() => [
              _createVNode(_unref(BTable), {
                items: filteredApiKeys.value,
                busy: props.loading,
                fields: apiKyesTablefields.value,
                small: "",
                striped: "",
                responsive: "",
                hover: "",
                class: "browser-table"
              }, {
                "cell(id)": _withCtx((data) => [
                  (!isKeyInactive(data.item))
                    ? (_openBlock(), _createBlock(_unref(BButton), {
                        key: 0,
                        variant: "outline-danger",
                        size: "sm",
                        onClick: ($event: any) => (revokeApiKeyButtonClicked(data.value))
                      }, {
                        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                          _createTextVNode(" Revoke ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true)
                ]),
                "cell(permissions)": _withCtx((data) => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.value.split(';'), (perm) => {
                    return (_openBlock(), _createBlock(_unref(BBadge), {
                      key: perm,
                      variant: apiKeyVariant(perm),
                      class: "me-1"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, {
                          icon: apiKeyIcon(perm),
                          class: "me-1"
                        }, null, 8, ["icon"]),
                        _createTextVNode(" " + _toDisplayString(perm), 1)
                      ]),
                      _: 2
                    }, 1032, ["variant"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["items", "busy", "fields"])
            ]),
            _: 1
          })
        ])),
    _createVNode(_unref(BFormCheckbox), {
      modelValue: showInactiveApiKeys.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showInactiveApiKeys).value = $event)),
      name: "check-button",
      switch: ""
    }, {
      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
        _createTextVNode(" show inactive access tokens ", -1)
      ]))]),
      _: 1
    }, 8, ["modelValue"]),
    (apiKeyIdToRemove.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 2,
          title: "Confirm action",
          "ok-title": "Confirm",
          "cancel-title": "Cancel",
          modelValue: showApiKeyRevokeModal.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showApiKeyRevokeModal).value = $event)),
          onOk: revokeApiKeyConfirmed,
          "ok-disabled": updatingToken.value
        }, {
          default: _withCtx(() => [
            (updatingToken.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                  _cache[6] || (_cache[6] = _createTextVNode(" Saving Changes ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (updateTokenShowSuccessIndicator.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
                    _cache[7] || (_cache[7] = _createTextVNode(" Changes Saved ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("p", _hoisted_5, "Are you sure you want to revoke the access token?")),
            _createVNode(_unref(BAlert), {
              modelValue: updateTokenError.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((updateTokenError).value = $event)),
              variant: "danger",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(updateTokenError.value), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["modelValue", "ok-disabled"]))
      : _createCommentVNode("", true)
  ]))
}
}

})